import { CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import truncateString from 'src/shared/utils/truncateString';

import { Avatar } from 'src/shared/components/ui';

import { Input, Typography } from '../ui';
import { ButtonV2 } from '../ui';

interface FileAttachmentInputProps {
  fileInputProps: any;
  captionInputProps: any;
  fileValue?: File & { preview: string };
  captionValue?: string;
  onRemove?: () => void;
  preview?: string;
}

const FileAttachmentInput: React.FC<FileAttachmentInputProps> = ({
  onRemove,
  captionInputProps,
  fileValue,
  captionValue,
  preview
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isImage = useMemo(() => {
    if (!fileValue) return false;

    return fileValue.type.startsWith('image/');
  }, [fileValue]);

  return (
    <div
      className="relative max-w-fit"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ButtonV2
        disableAnimation
        onPress={() => {
          if (!isEditing) {
            setIsEditing(true);
          }
        }}
        className="p-1 pr-2 relative hover:bg-white min-w-36 justify-start h-auto"
        startContent={
          isImage ? (
            <Avatar
              name={fileValue?.name}
              src={preview}
              classNames={{
                base: 'size-10 object-cover min-w-10 rounded-xl bg-gray-2 border border-gray-4'
              }}
            />
          ) : (
            <div className="bg-gray-2 size-10 flex items-center justify-center rounded-xl">
              <DocumentIcon className="size-5 text-primary" />
            </div>
          )
        }
        endContent={
          isEditing && (
            <div className="flex flex-col gap-2">
              <ButtonV2
                color="default"
                size="sm"
                onPress={() => setIsEditing(false)}
                isIconOnly
              >
                <CheckIcon className="size-4" />
              </ButtonV2>
              <ButtonV2
                onPress={() => {
                  onRemove?.();
                }}
                size="sm"
                color="default"
                isIconOnly
              >
                <TrashIcon className="size-4" />
              </ButtonV2>
            </div>
          )
        }
        size="xl"
        color="tertiary"
      >
        {isEditing ? (
          <div className="flex flex-col items-start gap-1">
            <Typography variant="body-2" weight="medium">
              {fileValue?.name}
            </Typography>
            <Input
              {...captionInputProps}
              placeholder="Enter caption"
              size="sm"
            />
          </div>
        ) : (
          <div className="flex flex-col text-left cursor-pointer">
            <Typography variant="body-2" weight="medium">
              {fileValue?.name}
            </Typography>
            {captionValue ? (
              <Typography
                variant="body-3"
                weight="medium"
                className="text-secondary-text"
              >
                {truncateString(captionValue, 30)}
              </Typography>
            ) : (
              <Typography
                variant="body-3"
                weight="medium"
                className="text-secondary-text"
              >
                Click to add caption
              </Typography>
            )}
          </div>
        )}
      </ButtonV2>

      {isHovered && onRemove && !isEditing && (
        <ButtonV2
          onPress={onRemove}
          size="xs"
          className="bg-black absolute -right-2 -top-2 w-5"
          isIconOnly
        >
          <XMarkIcon className="size-4 text-white" />
        </ButtonV2>
      )}
    </div>
  );
};

export default FileAttachmentInput;
